import { disableAnonymizedTracking, enableAnonymizedTracking, hasSnowplowInitialised } from '@ev-tech/snowplow-library';

import { getSnowplowWindow } from '@/utils/snowplowWindow';
import { SET_COOKIE_KEY, type SetCookieKey } from '@/consts/cookies';

type SameSite = 'Lax' | 'Strict' | 'None';

type DocumentCookieOptions = {
  keyName: SetCookieKey;
  value: string;
  maxAge?: number;
  path?: string;
  sameSite?: SameSite;
  secure?: boolean;
};

const APP_COOKIE_KEYS: ReadonlySet<SetCookieKey> = new Set(Object.values(SET_COOKIE_KEY));

export const getCookie = (keyName: string): string => {
  try {
    if (typeof document === 'undefined') return '';
    const regex = new RegExp(`(?:(?:^|.*;\\s*)${keyName}\\s*\\=\\s*([^;]*).*$)|^.*$`);
    return decodeURIComponent(document.cookie.replace(regex, '$1'));
  } catch {
    return '';
  }
};

export const writeDocumentCookie = ({ keyName, value, maxAge, path = '/', sameSite = 'Lax', secure = false }: DocumentCookieOptions) => {
  if (typeof document === 'undefined') {
    return;
  }

  if (!APP_COOKIE_KEYS.has(keyName)) {
    throw new Error(`Unsupported cookie key: ${keyName}`);
  }

  const expiration = typeof maxAge === 'number' ? `; expires=${new Date(Date.now() + maxAge * 1000).toUTCString()}` : '';

  // oxlint-disable-next-line unicorn/no-document-cookie
  document.cookie = `${keyName}=${encodeURIComponent(value)}; path=${path}${expiration}; SameSite=${sameSite}${secure ? '; Secure' : ''}`;
};

export const setLanguageCookie = (language: string) => {
  try {
    // This cookie is used by Digital Package to determine the language of a page
    // https://engelvoelkers.atlassian.net/browse/SEL-637
    writeDocumentCookie({
      keyName: SET_COOKIE_KEY.currentLanguage,
      value: language,
      path: '/',
      sameSite: 'Strict',
    });
  } catch (error) {
    console.error('Failed to set language cookie:', error);
  }
};

export const setCookie = (
  keyName: SetCookieKey,
  value: string,
  options?: { maxAge?: number; path?: string; sameSite?: SameSite; secure?: boolean },
) => {
  const { maxAge = 31_536_000, path = '/', sameSite = 'Lax', secure = true } = options || {};

  try {
    writeDocumentCookie({
      keyName,
      value,
      maxAge,
      path,
      sameSite,
      secure,
    });
  } catch (error) {
    console.error('Failed to set cookie:', error);
  }
};

export const setPrivateOfficeCookie = (privateOfficePassword: string, pathname: string) => {
  try {
    writeDocumentCookie({
      keyName: SET_COOKIE_KEY.privateOfficeAccess,
      value: privateOfficePassword,
      path: pathname,
      sameSite: 'Lax',
    });
  } catch (error) {
    console.error('Failed to set private office cookie:', error);
  }
};

// Modern tracking management - works with React context
export const updateSnowplowTracking = (hasUserAcceptedAgreement: boolean) => {
  if (process.env.NODE_ENV !== 'test' && typeof window !== 'undefined' && !hasSnowplowInitialised()) {
    const snowplowWindow = getSnowplowWindow();

    if (snowplowWindow?.initSnowplowTracker) {
      snowplowWindow.initSnowplowTracker({
        hasUserAcceptedAgreement,
      });
    }
  }

  if (process.env.NODE_ENV !== 'test') {
    if (hasUserAcceptedAgreement) {
      disableAnonymizedTracking();
      return;
    }

    enableAnonymizedTracking();
  }
};
